import { find, flow, get, isEmpty, reduce, sum } from 'lodash'
import { createSelector } from 'reselect'

import {
  ACTIVITY_LOG_ACTIVITES,
  BILLING_CODES_TYPES_MONITORING_DASHBOARD
} from 'src/constants'
import { makeOneLevelArray } from 'src/utils/Array'
import { addAllValue } from 'src/utils/DataTrees'

import { gridSettingsByName$ } from '../../user'

const activityDashboard = (state) => state.company.activityDashboard

export const activityList$ = createSelector(
  activityDashboard,
  (dashboard) => dashboard.activityList
)

export const activityStatistic$ = createSelector(
  activityDashboard,
  (dashboard) => dashboard.activityStatistic
)

export const activityBillingCodes$ = createSelector(
  activityDashboard,
  ({ billingCodes }) => billingCodes
)

export const isStatisticLoading$ = createSelector(
  activityDashboard,
  ({ isStatisticLoading }) => isStatisticLoading
)

export const dashboardBillingCodes$ = createSelector(
  activityDashboard,
  ({ billingCodes = [] }) =>
    makeOneLevelArray(billingCodes).map((item) => ({
      ...item,
      value:
        item.addOn && !isEmpty(item.addOn)
          ? `${item.code},${item.addOn.code}`
          : item.code,
      title:
        item.addOn && !isEmpty(item.addOn)
          ? `${item.code} - ${item.description} / ${item.addOn.code} - ${item.addOn.description}`
          : `${item.code} - ${item.description}`
    }))
)

export const dashboardBillingCodesWithAll$ = createSelector(
  dashboardBillingCodes$,
  (dashboardBillingCodes = []) => addAllValue(dashboardBillingCodes)
)

export const activityDashboardBillingCodes$ = createSelector(
  activityDashboard,
  ({ billingCodes = [] }) =>
    makeOneLevelArray(billingCodes).map((item) => ({
      ...item,
      value: item.uuid,
      title:
        item.addOn && !isEmpty(item.addOn)
          ? `${item.code} - ${item.description} / ${item.addOn.code} - ${item.addOn.description}`
          : `${item.code} - ${item.description}`
    }))
)

const convertStatistic = (statistic) => ({
  activity: reduce(
    statistic,
    (result, item, key) => {
      const config = find(ACTIVITY_LOG_ACTIVITES, ['value', key])

      if (config) {
        result.push({ ...item, ...config })
      }

      return result
    },
    []
  ),
  revenue: reduce(
    statistic.revenue,
    (result, item, key) => {
      result.push({ title: key, value: +item })

      return result
    },
    []
  )
})

export const activityBillingStatistic$ = createSelector(
  activityDashboard,
  ({ activityBillingStatistic: { rpm = {}, ccm = {} } }) => {
    const updRpm = convertStatistic(rpm)
    const updCcm = convertStatistic(ccm)

    return {
      rpm: updRpm,
      ccm: updCcm
    }
  }
)

export const predefinedActivityDashboardFilter$ = createSelector(
  activityDashboard,
  ({ predefinedActivityDashboardFilter }) => predefinedActivityDashboardFilter
)

export const activitySummary$ = createSelector(
  activityDashboard,
  dashboardBillingCodes$,
  gridSettingsByName$('monitoringDashboardOld'),
  ({ activitySummary = {} }, dashboardBillingCodes = {}, { columns = [] }) => {
    const activeTypes = columns.map((name = '') =>
      BILLING_CODES_TYPES_MONITORING_DASHBOARD.get(name)
    )
    const summary = reduce(
      activitySummary,
      (result, value, key) => {
        result[key] = reduce(
          value,
          (result, value, code) => {
            const billingCode = dashboardBillingCodes.find(
              ({ code: billingCode }) => billingCode === code
            )
            const isEnabled = activeTypes.includes(billingCode?.type)

            if (isEnabled) {
              const title = flow(
                (items = []) =>
                  items.find(({ code: billingCode }) => billingCode === code),
                (items = []) => {
                  const code = get(items, ['code'], '')
                  const codeFamilyName = get(items, ['codeFamilyName'], '')
                  const description = get(items, ['description'], '')

                  return `${
                    code === '99457' ? codeFamilyName : code
                  } - ${description}`
                }
              )(dashboardBillingCodes)
              const updVlaue = {
                code,
                all: reduce(
                  value,
                  (result, value) => {
                    result += +value

                    return result
                  },
                  0
                ),
                title,
                ...value
              }

              result.push(updVlaue)
            }

            return result
          },
          []
        )

        return result
      },
      {}
    )

    return summary
  }
)

export const dashboardMeasurementList$ = createSelector(
  activityDashboard,
  ({ dashboardMeasurementList = [] }) =>
    dashboardMeasurementList.map((el) => ({ ...el, value: el.type }))
)

export const dashboardMeasurementListWithAll$ = createSelector(
  dashboardMeasurementList$,
  (dashboardMeasurementList = []) => addAllValue(dashboardMeasurementList)
)

export const activityStatistics$ = createSelector(
  activityDashboard,
  (dashboard) => {
    const statistics = dashboard?.statistics || {}

    const total = sum(Object.values(statistics))

    return {
      ...statistics,
      total
    }
  }
)
